import {Injectable} from '@angular/core';
import {plainToInstance} from 'class-transformer';
import {ManufacturedItemGroupQuery} from './models/manufactured-item-group.query';
import {ManufacturingItemGroupModel} from 'web-shared';
import {
  FilterModel,
  getBase64,
  GLOBAL,
  MesApiService,
  Page,
  PagedData,
  stringCompare
} from 'common-shared';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {Observable} from "rxjs";
import {ItemGroupsQuery} from 'web-shared';
import {SpecLineModel} from "web-shared";
import {
  ItemGroupListFilter,
  ItemGroupListQuery,
} from "web-shared";

@Injectable({
  providedIn: 'root'
})
export class ManufacturingItemGroupService extends MesApiService {
  constructor(public query: ItemGroupsQuery) {
    super('ItemGroup', 'manufacturing');
  }

  getAll$ = this.query.selectAll();
  getAllGroups$ = this.query.selectAll({filterBy: entity => entity.TypeClv === GLOBAL.Classificators.ItemGroupType.RawMaterial}).pipe(shareReplay(1));
  allNotSpecialProcess$ = this.query.selectAll({filterBy: entity => entity.TypeClv !== GLOBAL.Classificators.ItemGroupType.SpecialProcess}).pipe(shareReplay(1));
  allSpecialProcess$ = this.query.selectAll({filterBy: entity => entity.TypeClv === GLOBAL.Classificators.ItemGroupType.SpecialProcess}).pipe(shareReplay(1));
  getNotDrafts$ = this.query.getAll$.pipe(map(itemGroups => {
    return (itemGroups.filter(itemGroup => itemGroup.StatusClv !== GLOBAL.Classificators.ItemGroupStatus.Draft) || [])
      .sort((a, b) => stringCompare(a, b, obj => obj.DisplayName));
  }), shareReplay(1));

  loadOne(id: string): Observable<ManufacturingItemGroupModel> {
    return this.getSingle<ManufacturingItemGroupModel>({
      id: id
    }).pipe(map(value => {
      return plainToInstance(ManufacturingItemGroupModel, value.data);
    }));
  }

  addNew(manufacturedItemGroupModel: ManufacturingItemGroupModel) {
    return this.add<ManufacturingItemGroupModel>(manufacturedItemGroupModel).pipe(map(value => {
      this.query.loadAll(value.data.Id).subscribe();
      return plainToInstance(ManufacturingItemGroupModel, value.data);
    }));
  }

  save(manufacturedItemGroupModel: ManufacturingItemGroupModel) {
    return this.update<ManufacturingItemGroupModel>(manufacturedItemGroupModel.Id, manufacturedItemGroupModel, undefined,{clear: true}).pipe(map(value => {
      return plainToInstance(ManufacturingItemGroupModel, value.data);
    }));
  }

  remove(manufacturedItemGroupModel: ManufacturingItemGroupModel) {
    return this.delete<ManufacturingItemGroupModel>(manufacturedItemGroupModel.Id).pipe(map(value => {
      return plainToInstance(ManufacturingItemGroupModel, value.data);
    }));
  }

  queryOneById(itemGroupId: string) {
    return this.getSingle<ManufacturedItemGroupQuery>({
      id: itemGroupId,
      action: 'GetWithProcedureItemProperties'
    }).pipe(map(value => {
      return plainToInstance(ManufacturedItemGroupQuery, value);
    }));
  }

  copy(id: string, newCode: string, newName: string): Observable<string> {
    return this.getSingle<ManufacturingItemGroupModel>({
      id: id,
      action: 'Copy'
    }, {newCode: newCode, newName: newName}).pipe(map(value => {
      return value.data.Id.toString();
    }));
  }

  copyAsDraft(id: string): Observable<string> {
    return this.getSingle<ManufacturingItemGroupModel>({
      id: id,
      action: 'CopyAsDraft'
    }).pipe(map(value => {
      return value.data.Id.toString();
    }));
  }

  publish(id: string): Observable<string> {
    return this.getSingle<ManufacturingItemGroupModel>({
      id: id,
      action: 'Publish'
    }).pipe(map(value => {
      return value.data.toString();
    }));
  }
  getIdsBySelectedHierarchyNodes(nodeIds: string[]): Observable<string[]> {
    return this.add(nodeIds, {action: 'getIdsBySelectedHierarchyNodes'}).pipe(map(value => {
      return [...value.data]
    }));
  }

  getListByFilter(filter: FilterModel<ItemGroupListFilter>): Observable<PagedData<ItemGroupListQuery>> {
    return this.addGet<FilterModel<ItemGroupListFilter>, PagedData<ItemGroupListQuery>>(filter, {action: 'GetListByFilter'}).pipe(map(value => {
      return new PagedData<ItemGroupListQuery>(plainToInstance(ItemGroupListQuery, value.data.Data), plainToInstance(Page, value.data.Page), value.data.DistinctSpecs);
    }));
  }

  downloadListExcel(filter: FilterModel<ItemGroupListFilter>, includeImeTab: boolean) {
    return this.http.post(this.getRoute({action: 'ExportListToExcel'}) + '?includeImeTab=' + includeImeTab, filter, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(value => {
      return this.getFileNameAndData(value);
    }));
  }

  uploadExcel(file: File): Observable<number> {
    return getBase64(file).pipe(switchMap(fileData => {
      return this.addGet<string, number>(
        fileData,
        { action: 'ImportFromExcel' }
      ).pipe(map(value => {
        return value.data;
      }));
    }));
  }

  getSpecs(id: string): Observable<SpecLineModel[]> {
    return this.getList<SpecLineModel>({action: 'GetSpecs', id: id}).pipe(map(value1 => {
      return plainToInstance(SpecLineModel, value1.data);
    }));
  }
}
